import { useQueries } from "react-query";
import { useAccessToken } from "hooks/useAccessToken";
import { BACKEND_DOMAIN, QUERY_STALE_TIME } from "utils/constants";
import { buildRequestHeaders } from "utils/helpers";
import { formatNumberWithCommas } from "utils/formatting";
import pageStyles from "pages/BuildingPage/VirtualAudit/VirtualAuditPage.module.scss";
import styles from "./PropertyProjectsSection.module.scss";
import ProjectsSkeletonTable from "./ProjectsSkeletonTable";

interface PropertyProjectsSectionProps {
    buildingModelUids: string[];
}

const fmt = (v: number | null | undefined) => (v != null ? formatNumberWithCommas(Math.round(v), 0) : "—");
const fmtK = (v: number | null | undefined) =>
    v != null ? `$${formatNumberWithCommas(Math.round(v / 1000), 0)}k` : "—";

interface AggregatedMeasure {
    carbonReductionMeasureType: string;
    yearApplied: number;
    annualMeanCarbonEmissionSavings: number;
    energyConsumptionSavingsTotal: number;
    annualMeanUtilityCostSavings: number;
    annualMeanCarbonPolicySavings: number;
    annualMeanRevenue: number;
    measureCost: number;
    totalIncentives: number | null;
    netTotalCost: number;
}

function PropertyProjectsSection({ buildingModelUids }: PropertyProjectsSectionProps) {
    const accessToken = useAccessToken();

    const buildingQueries = useQueries(
        buildingModelUids.map((uid) => ({
            queryKey: ["buildingModel", uid],
            queryFn: async (): Promise<Building> => {
                const res = await fetch(`${BACKEND_DOMAIN}/buildings/${uid}`, {
                    headers: buildRequestHeaders(accessToken),
                });
                if (!res.ok) throw new Error(`Error ${res.status}`);
                return res.json();
            },
            staleTime: QUERY_STALE_TIME,
        }))
    );

    const isLoading = buildingQueries.some((q) => q.isLoading);
    const buildings = buildingQueries.map((q) => q.data).filter((b): b is Building => b != null);

    const allMeasures = buildings.flatMap((building) => {
        const reportedPlan = building.carbonReductionPlans.find((p) => p.isReported);
        return (reportedPlan?.plan?.carbonReductionMeasures ?? []).filter(
            (m) =>
                m.carbonReductionMeasureType !== "No Action" && m.carbonReductionMeasureType !== "Base building state"
        );
    });

    const aggregated: AggregatedMeasure[] = Object.values(
        allMeasures.reduce<Record<string, AggregatedMeasure>>((acc, m) => {
            const key = m.carbonReductionMeasureType;
            if (!acc[key]) {
                acc[key] = {
                    carbonReductionMeasureType: key,
                    yearApplied: m.yearApplied,
                    annualMeanCarbonEmissionSavings: 0,
                    energyConsumptionSavingsTotal: 0,
                    annualMeanUtilityCostSavings: 0,
                    annualMeanCarbonPolicySavings: 0,
                    annualMeanRevenue: 0,
                    measureCost: 0,
                    totalIncentives: null,
                    netTotalCost: 0,
                };
            }
            acc[key].yearApplied = Math.min(acc[key].yearApplied, m.yearApplied);
            acc[key].annualMeanCarbonEmissionSavings += m.annualMeanCarbonEmissionSavings ?? 0;
            acc[key].energyConsumptionSavingsTotal += m.energyConsumptionSavingsTotal ?? 0;
            acc[key].annualMeanUtilityCostSavings += m.annualMeanUtilityCostSavings ?? 0;
            acc[key].annualMeanCarbonPolicySavings +=
                m.measureLife > 0 ? (m.lifetimeCarbonCostsAvoided ?? 0) / m.measureLife : 0;
            acc[key].annualMeanRevenue += m.measureLife > 0 ? (m.lifetimeRevenue ?? 0) / m.measureLife : 0;
            acc[key].measureCost += m.measureCost ?? 0;
            if (m.totalIncentives != null) {
                acc[key].totalIncentives = (acc[key].totalIncentives ?? 0) + m.totalIncentives;
            }
            acc[key].netTotalCost += m.netTotalCost ?? 0;
            return acc;
        }, {})
    ).sort((a, b) => a.yearApplied - b.yearApplied);

    const cols = [
        { label: "Measure", key: "carbonReductionMeasureType" },
        { label: "Year", key: "yearApplied" },
        { label: "Emissions Savings (tCO₂e)", key: "annualMeanCarbonEmissionSavings", format: fmt },
        { label: "Energy Savings (kWh)", key: "energyConsumptionSavingsTotal", format: fmt },
        { label: "Annual Utility Cost Savings", key: "annualMeanUtilityCostSavings", format: fmtK },
        { label: "Annual Carbon Policy Savings", key: "annualMeanCarbonPolicySavings", format: fmtK },
        { label: "Annual Revenue", key: "annualMeanRevenue", format: fmtK },
        { label: "Total Cost", key: "measureCost", format: fmtK },
        { label: "Incentives", key: "totalIncentives", format: fmtK },
        { label: "Net Total Cost", key: "netTotalCost", format: fmtK },
    ] as const;

    if (isLoading) {
        return (
            <div>
                <h2 className={pageStyles.sectionTitle}>Projects for Consideration</h2>
                <div className={styles.tableWrapper}>
                    <ProjectsSkeletonTable cols={cols} />
                </div>
            </div>
        );
    }

    if (!aggregated.length) return null;

    return (
        <div>
            <h2 className={pageStyles.sectionTitle}>Projects for Consideration</h2>
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            {cols.map((c) => (
                                <th key={c.key} className={styles.th}>
                                    {c.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {aggregated.map((row) => (
                            <tr key={row.carbonReductionMeasureType} className={styles.tr}>
                                {cols.map((c) => {
                                    const raw = (row as any)[c.key];
                                    const formatted = "format" in c ? c.format(raw as number) : null;
                                    const display = raw == null ? "—" : (formatted ?? String(raw));
                                    return (
                                        <td key={c.key} className={styles.td}>
                                            {display}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default PropertyProjectsSection;
